import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const slides = [
  {
    id: 1,
    title: 'Bienvenida.',
    content: [
      'Este es un espacio para bajar el ritmo, respirar y volver a ti.',
      'No necesitas saber nada especial.',
      'No necesitas creer en nada nuevo.',
      'Solo estar aquí, tal como estás.'
    ]
  },
  {
    id: 2,
    title: 'Quién acompaña',
    content: [
      'Mi nombre es Herminio y camino contigo en este espacio.',
      'Me formé como arquitecto técnico, aprendiendo a construir, medir y sostener estructuras.',
      'Con el tiempo, algo en mí pidió más profundidad y sentido. Ese llamado me llevó a la sanación energética, los Registros Akáshicos y otros caminos de consciencia.',
      'Hoy comprendo que no dejé la estructura atrás. Aprendí a llevarla al interior.'
    ]
  },
  {
    id: 3,
    title: 'Sentido del espacio',
    content: [
      'Este espacio no existe para sacarte de tu vida.',
      'Existe para ayudarte a habitarla con más presencia, claridad y calma.',
      'Aquí no trabajamos para elevarnos fuera del cuerpo.',
      'Trabajamos para sentirnos seguros dentro de él.'
    ]
  },
  {
    id: 4,
    title: 'Cómo acompaño',
    content: [
      'Todo lo que comparto nace de la experiencia real, no de la teoría.',
      'Integro estructura y sensibilidad, consciencia y cuerpo, energía y vida cotidiana.',
      'Acompaño desde la claridad, la cercanía, la alegría y el humor consciente.',
      'La verdad no tiene por qué ser pesada.'
    ]
  },
  {
    id: 5,
    title: 'Autonomía',
    content: [
      'Mi intención no es que dependas de mí.',
      'Es que confíes cada vez más en ti.',
      'Aquí no entregas tu poder personal.',
      'Aquí lo recuperas, a tu ritmo.',
      'Yo acompaño. Tú eres quien camina.'
    ]
  },
  {
    id: 6,
    title: 'Invitación final',
    content: [
      'Este es un espacio para escucharte, ordenar lo que sientes y sostenerte con más amabilidad.',
      'No tienes que convertirte en nadie distinto.',
      'Solo volver a casa dentro de ti.',
      'Gracias por estar aquí.'
    ],
    isFinal: true
  }
];

const Onboarding = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const navigate = useNavigate();
  const { completeOnboarding } = useAuth();

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setDirection(1);
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handleSkip = async () => {
    await completeOnboarding();
    navigate('/mi-camino');
  };

  const handleEnter = async () => {
    await completeOnboarding();
    navigate('/mi-camino');
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 100 : -100,
      opacity: 0
    })
  };

  const slide = slides[currentSlide];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Skip button */}
      <div className="absolute top-6 right-6 z-10">
        <button
          onClick={handleSkip}
          className="flex items-center gap-2 text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-secondary))] transition-colors text-sm"
          data-testid="onboarding-skip"
        >
          Saltar onboarding
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-[hsl(var(--sage-light))]">
        <div 
          className="h-full bg-[hsl(var(--accent))] progress-fill"
          style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-24">
        <div className="max-w-2xl w-full">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentSlide}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="text-center"
            >
              {/* Slide indicator */}
              <div className="flex justify-center gap-2 mb-12">
                {slides.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentSlide
                        ? 'w-8 bg-[hsl(var(--accent))]'
                        : index < currentSlide
                        ? 'bg-[hsl(var(--sage))]'
                        : 'bg-[hsl(var(--sage-light))]'
                    }`}
                  />
                ))}
              </div>

              {/* Title */}
              <h1 
                className="text-4xl lg:text-5xl font-heading text-[hsl(var(--text-primary))] mb-10"
                data-testid={`onboarding-title-${currentSlide}`}
              >
                {slide.title}
              </h1>

              {/* Content */}
              <div className="space-y-6 mb-12">
                {slide.content.map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-lg text-[hsl(var(--text-secondary))] leading-relaxed"
                    data-testid={`onboarding-content-${currentSlide}-${index}`}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* Button */}
              {slide.isFinal ? (
                <Button
                  onClick={handleEnter}
                  className="btn-gold text-lg px-12 py-6"
                  data-testid="onboarding-enter"
                >
                  Entrar a la Escuela
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  className="btn-gold text-lg px-12 py-6 flex items-center gap-2 mx-auto"
                  data-testid="onboarding-continue"
                >
                  Continuar
                  <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                </Button>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[hsl(var(--sage-light))]/50 to-transparent pointer-events-none" />
    </div>
  );
};

export default Onboarding;
